/**
 * 施術録設定 modal — clinic-wide print (launched from /treatment_records/init).
 * Legacy parity: RA2 TreatmentRecordController@openModal.
 *
 * Structure:
 *   - Header: title 施術録設定
 *   - Body: ■表面 (6 groups ①~⑥) + ■裏面 (15-17 flags)
 *   - Footer: 閉じる (left) + ○表面 / ○裏面 / ○両面 radio + 印刷 (right)
 *
 * 期間指定 picker is intentionally NOT rendered for clinic-wide (A2 legacy
 * `list_template.blade.php` shows it only when $param['patient_id'] is set,
 * i.e. per-patient modal). The user has already constrained the scope via
 * rezept selection on 施術録検索; no additional period sub-filter is needed.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AppButton,
  AppCheckbox,
  AppModal,
  AppRadio,
  AppRadioGroup,
  AppSpin,
  AppTextField,
} from '@/components/atoms';
import {
  useTreatmentRecordPrintClinicWideInit,
  useTreatmentRecordPrintClinicWideOutput,
} from '@/hooks/main/useTreatmentRecordPrintClinicWide';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  A2_VISIT_CLINIC_SYNC_FIELDS,
  ALL_FLAG_FIELDS,
  BACK_FLAG_FIELDS,
  BACK_FLAG_FIELDS_A2,
  BACK_FLAG_FIELDS_R2,
  FRONT_FLAG_FIELDS,
  FRONT_GROUP_3_FIELDS,
  FRONT_GROUP_4_FIELDS,
  FRONT_GROUP_5_FIELDS,
  FRONT_GROUP_6_FIELDS,
  FRONT_GROUP_6_R2_FIELDS,
  FRONT_GROUP_7_FIELDS,
  OUTPUT_RADIO_BACK,
  OUTPUT_RADIO_BOTH,
  OUTPUT_RADIO_FRONT,
  type BackFlagField,
  type OutputRadio,
  type PrintFlagField,
} from '../constants';
import type {
  TreatmentRecordPrintClinicWidePayload,
  TreatmentRecordPrintFlags,
} from '../types';

interface Props {
  isOpen: boolean;
  rezeptIds: number[];
  onClose: () => void;
}

/** Initial flag state — all OFF until init data hydrates. */
const EMPTY_FLAGS: TreatmentRecordPrintFlags = {};

// ────────────────────────────────────────────────────────────────────────────
// A2 preview config — はり・きゅう.
// Template layout, range overlay images, and flag→range mapping are A2-owned.
// DO NOT reference from R2 config (and vice versa) — the two services evolve
// independently. Copy changes across explicitly if they need to stay aligned.
// ────────────────────────────────────────────────────────────────────────────
type A2RangeKey = 1 | 2 | 3 | 4 | 5 | 6;
const A2_PREVIEW_DIR = '/images/treatment-record-print';
const A2_PREVIEW_BASE = `${A2_PREVIEW_DIR}/print_front_base.png`;
const A2_PREVIEW_RANGE = (n: A2RangeKey) => `${A2_PREVIEW_DIR}/print_range${n}.png`;
const A2_RANGE_KEYS: readonly A2RangeKey[] = [1, 2, 3, 4, 5, 6];
const A2_RANGE_FLAG_MAP: Record<A2RangeKey, readonly PrintFlagField[]> = {
  1: ['is_front_clinic_id'],
  2: ['is_health_insurance_cards_type'],
  3: FRONT_GROUP_3_FIELDS,
  4: FRONT_GROUP_4_FIELDS,
  5: FRONT_GROUP_5_FIELDS,
  6: FRONT_GROUP_6_FIELDS,
};

// ────────────────────────────────────────────────────────────────────────────
// R2 preview config — マッサージ.
// Ported from R2 legacy (`R2/resources/views/treatment_records/list_template.blade.php`).
// R2 splits 施術の種類 (マッサージ/変形徒手矯正術/温罨法/往療距離) into its own
// ⑦ group, so the base template + range 6 + range 7 all differ from A2.
// Assets live under /r2/ subfolder and MUST NOT share references with A2.
// ────────────────────────────────────────────────────────────────────────────
type R2RangeKey = 1 | 2 | 3 | 4 | 5 | 6 | 7;
const R2_PREVIEW_DIR = '/images/treatment-record-print/r2';
const R2_PREVIEW_BASE = `${R2_PREVIEW_DIR}/print_front_base.png`;
const R2_PREVIEW_RANGE = (n: R2RangeKey) => `${R2_PREVIEW_DIR}/print_range${n}.png`;
const R2_RANGE_KEYS: readonly R2RangeKey[] = [1, 2, 3, 4, 5, 6, 7];
const R2_RANGE_FLAG_MAP: Record<R2RangeKey, readonly PrintFlagField[]> = {
  1: ['is_front_clinic_id'],
  2: ['is_health_insurance_cards_type'],
  3: FRONT_GROUP_3_FIELDS,
  4: FRONT_GROUP_4_FIELDS,
  5: FRONT_GROUP_5_FIELDS,
  6: FRONT_GROUP_6_R2_FIELDS,
  7: FRONT_GROUP_7_FIELDS,
};

const TreatmentRecordPrintModal: React.FC<Props> = ({ isOpen, rezeptIds, onClose }) => {
  const initQuery = useTreatmentRecordPrintClinicWideInit(isOpen ? rezeptIds : null);
  const { outputPdfMutation, isLoading: isPrinting } =
    useTreatmentRecordPrintClinicWideOutput();
  const { serviceType } = useTheme();
  const isR2 = serviceType === SERVICE_TYPE_R2;
  const initData = initQuery.data;

  const [flags, setFlags] = useState<TreatmentRecordPrintFlags>(EMPTY_FLAGS);
  const [officeName, setOfficeName] = useState('');
  const [doctorNameKana, setDoctorNameKana] = useState('');
  const [length, setLength] = useState('');
  const [outputRadio, setOutputRadio] = useState<OutputRadio>(OUTPUT_RADIO_FRONT);

  // 印刷面 (表面/裏面/両面) is NOT persisted across modal opens — spec wants it
  // reset to 表面 each time so an accidental 両面 from a previous session doesn't
  // carry over. Independent from initData loading.
  useEffect(() => {
    if (isOpen) setOutputRadio(OUTPUT_RADIO_FRONT);
  }, [isOpen]);

  // Hydrate form from init data whenever modal opens with new selection.
  // Load saved flags from treatment_record_print_adjustments per R2 spec
  // ("Setting 表面: default check ON all; checkbox state persists for next open").
  // Backend falls back to "all-on" defaults for first-time users. A2 follows the
  // same convention — `defaultFlagValues()` picks the right ON-set per service.
  useEffect(() => {
    if (!isOpen || !initData) return;

    setFlags({ ...(initData.flags ?? {}) });
    setOfficeName(initData.office_name ?? '');
    setDoctorNameKana(initData.consents_doctor_name_kana ?? '');
    setLength(initData.length ?? '');
  }, [isOpen, initData]);

  const setFlag = useCallback((field: PrintFlagField, checked: boolean) => {
    setFlags(prev => ({ ...prev, [field]: checked }));
  }, []);

  const setGroupFlags = useCallback(
    (fields: readonly PrintFlagField[], checked: boolean) => {
      setFlags(prev => {
        const next = { ...prev };
        fields.forEach(f => {
          next[f] = checked;
        });
        return next;
      });
    },
    []
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const payload: TreatmentRecordPrintClinicWidePayload = {
      rezept_ids: rezeptIds,
      output_radio: outputRadio,
      office_name: officeName || null,
      consents_doctor_name_kana: doctorNameKana || null,
      length: length || null,
      ...ALL_FLAG_FIELDS.reduce<TreatmentRecordPrintFlags>((acc, f) => {
        acc[f] = !!flags[f];
        return acc;
      }, {}),
    };

    try {
      const result = await outputPdfMutation(payload);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch {
      // toast handled by hook onError (empty-data 422 surfaces NO_DATA_MESSAGE)
    }
  }, [
    rezeptIds,
    outputRadio,
    officeName,
    doctorNameKana,
    length,
    flags,
    outputPdfMutation,
  ]);

  const isLoadingInit = initQuery.isLoading || initQuery.isFetching;

  // ────────────────────────────────────────────────────────────────────────
  // Preview overlay visibility — a range is visible when ANY of its linked
  // flag fields is checked (matches RA2 legacy culum_control behaviour).
  // ────────────────────────────────────────────────────────────────────────
  // Each service picks its own preview config — A2 and R2 assets/logic are fully isolated.
  const previewBase = isR2 ? R2_PREVIEW_BASE : A2_PREVIEW_BASE;
  const previewRangeSrc = (n: number) =>
    isR2 ? R2_PREVIEW_RANGE(n as R2RangeKey) : A2_PREVIEW_RANGE(n as A2RangeKey);
  const rangeKeys: readonly number[] = isR2 ? R2_RANGE_KEYS : A2_RANGE_KEYS;
  const rangeVisible = useMemo<Record<number, boolean>>(() => {
    const visible: Record<number, boolean> = {};
    const map = isR2 ? R2_RANGE_FLAG_MAP : A2_RANGE_FLAG_MAP;
    rangeKeys.forEach(k => {
      const fields = (map as Record<number, readonly PrintFlagField[]>)[k];
      visible[k] = fields ? fields.some(f => !!flags[f]) : false;
    });
    return visible;
  }, [flags, isR2, rangeKeys]);

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      title="施術録設定"
      maxWidth={1200}
      // `treatment-record-print-modal` adds body min/max-height + removes
      // bottom padding (see scss/index.scss) so the sticky action bar inside
      // the body sits flush with the modal edge. `app-modal-root` is kept so
      // AppModal's shared modal styles still apply.
      rootClassName="app-modal-root treatment-record-print-modal"
      // Footer slot disabled — Ant Modal's footer wrapping kept overflowing the
      // 印刷 button outside the modal regardless of styling tweaks. Render the
      // action bar inline at the bottom of the body as a sticky element instead,
      // so layout is fully under our control.
      footer={null}
    >
      {isLoadingInit ? (
        <div
          className="flex min-h-[480px] items-center justify-center"
          aria-busy="true"
        >
          <AppSpin size="large" />
        </div>
      ) : (
        <>
        <div className="grid grid-cols-12 gap-4">
          {/* ===== LEFT: preview image stack — scrolls together with the form ===== */}
          <div className="col-span-12 md:col-span-5">
            <h3 className="mb-2 text-sm font-semibold">■施術録</h3>
            <div className="relative w-full">
              <img
                src={previewBase}
                alt="施術録 preview"
                className="block w-full select-none"
                draggable={false}
              />
              {rangeKeys.map(n => (
                <img
                  key={n}
                  src={previewRangeSrc(n)}
                  alt=""
                  aria-hidden
                  draggable={false}
                  className="pointer-events-none absolute inset-0 w-full select-none transition-opacity"
                  style={{ opacity: rangeVisible[n] ? 1 : 0 }}
                />
              ))}
            </div>
          </div>

          {/* ===== RIGHT: form ===== */}
          <div className="col-span-12 flex flex-col gap-4 md:col-span-7">
            {/* ■表面 */}
            <section className="rounded border border-gray-200 p-3">
              <p className="mb-2 text-xs text-red-600">
                ※表面裏面全てOFFの場合は雛形印刷されます
              </p>
              <div className="mb-2 flex items-center gap-2">
                <h3 className="text-sm font-semibold">■表面 全印刷</h3>
                <AppButton size="small" onClick={() => setGroupFlags(FRONT_FLAG_FIELDS, true)}>
                  ON
                </AppButton>
                <AppButton size="small" onClick={() => setGroupFlags(FRONT_FLAG_FIELDS, false)}>
                  OFF
                </AppButton>
              </div>

              <FrontGroup
                flags={flags}
                setFlag={setFlag}
                officeName={officeName}
                setOfficeName={setOfficeName}
                doctorNameKana={doctorNameKana}
                setDoctorNameKana={setDoctorNameKana}
                length={length}
                setLength={setLength}
                isR2={isR2}
              />
            </section>

            {/* ■裏面 */}
            <section className="rounded border border-gray-200 p-3">
              <div className="mb-2 flex items-center gap-2">
                <h3 className="text-sm font-semibold">■裏面 全印刷</h3>
                <AppButton size="small" onClick={() => setGroupFlags(BACK_FLAG_FIELDS, true)}>
                  ON
                </AppButton>
                <AppButton size="small" onClick={() => setGroupFlags(BACK_FLAG_FIELDS, false)}>
                  OFF
                </AppButton>
              </div>

              <BackGroup flags={flags} setFlag={setFlag} isR2={isR2} />
            </section>
          </div>
        </div>

        {/* Action bar — replaces Ant Modal's footer slot to avoid its overflow quirks. */}
        <div
          className="sticky bottom-0 z-10 mt-4 flex items-center justify-between gap-4 border-t border-gray-200 bg-white py-3"
        >
          <AppButton onClick={handleClose}>閉じる</AppButton>
          <div className="flex items-center gap-4">
            <AppRadioGroup
              value={outputRadio}
              onChange={e => setOutputRadio(e.target.value as OutputRadio)}
            >
              <AppRadio value={OUTPUT_RADIO_FRONT}>表面</AppRadio>
              <AppRadio value={OUTPUT_RADIO_BACK}>裏面</AppRadio>
              <AppRadio value={OUTPUT_RADIO_BOTH}>両面</AppRadio>
            </AppRadioGroup>
            <AppButton
              type="primary"
              onClick={handleSubmit}
              loading={isPrinting}
              disabled={isLoadingInit}
              icon={<i className="fa fa-print" />}
            >
              印刷
            </AppButton>
          </div>
        </div>
        </>
      )}
    </AppModal>
  );
};

// ---------------------------------------------------------------------------
// ① ~ ⑥ Front groups
// ---------------------------------------------------------------------------

interface FrontGroupProps {
  flags: TreatmentRecordPrintFlags;
  setFlag: (field: PrintFlagField, checked: boolean) => void;
  officeName: string;
  setOfficeName: (v: string) => void;
  doctorNameKana: string;
  setDoctorNameKana: (v: string) => void;
  length: string;
  setLength: (v: string) => void;
  isR2: boolean;
}

const FrontGroup: React.FC<FrontGroupProps> = ({
  flags,
  setFlag,
  officeName,
  setOfficeName,
  doctorNameKana,
  setDoctorNameKana,
  length,
  setLength,
  isR2,
}) => {
  const cb = (f: PrintFlagField, label: string) => (
    <AppCheckbox checked={!!flags[f]} onChange={e => setFlag(f, e.target.checked)}>
      {label}
    </AppCheckbox>
  );

  /**
   * Master toggle for a group (③~⑥): single click toggles ALL subfields.
   * Matches A2 legacy `is_*_check` master checkbox (UI-only — not persisted).
   * State = checked if ALL subfields on, indeterminate if some.
   */
  const masterCheckbox = (fields: readonly PrintFlagField[], label: string) => {
    const allChecked = fields.every(f => !!flags[f]);
    const someChecked = fields.some(f => !!flags[f]);
    return (
      <AppCheckbox
        checked={allChecked}
        indeterminate={!allChecked && someChecked}
        onChange={e => fields.forEach(f => setFlag(f, e.target.checked))}
      >
        {label}
      </AppCheckbox>
    );
  };

  return (
    <div className="flex flex-col gap-3 text-sm">
      {/* ① */}
      <div>①&nbsp;{cb('is_front_clinic_id', '治療院ID-受診者No を印刷')}</div>

      {/* ② */}
      <div>②&nbsp;{cb('is_health_insurance_cards_type', '保険種別 を印刷')}</div>

      {/* ③ */}
      <div>
        ③&nbsp;{masterCheckbox(FRONT_GROUP_3_FIELDS, '公費負担医療 を印刷')}
        <div className="ml-6 flex flex-wrap gap-x-4 gap-y-1">
          {cb('is_medical_aid_number', '公費負担者番号')}
          {cb('is_medical_aid_patient_number', '公費受給者番号')}
        </div>
      </div>

      {/* ④ — BD section 27 item 4.1~4.12 order */}
      <div>
        ④&nbsp;{masterCheckbox(FRONT_GROUP_4_FIELDS, '保険証面 を印刷')}
        <div className="ml-6 mt-1">
          <div className="text-xs text-gray-500">（施術を受ける者）</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {cb('is_health_insurance_card_mark', '記号')}
            {cb('is_health_insurance_card_number', '番号')}
            {cb('is_health_insurance_card_qualification_date', '資格取得年月日')}
            {cb('is_health_insurance_card_expiration_date', '有効期限')}
            {cb('is_patient_information_name', '氏名')}
            {cb('is_patient_information_name_kana', '氏名（フリガナ）')}
            {cb('is_patient_sex', '性別')}
            {cb('is_patient_information_date_of_birth', '生年月日')}
          </div>
          <div className="mt-1 flex items-center gap-2">
            {cb('is_office_name', '事業所')}
            <AppTextField
              value={officeName}
              onChange={e => setOfficeName(e.target.value)}
              maxLength={255}
              className="w-60"
              placeholder="事業所名"
            />
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {cb('is_patient_information_relationship_id', '続柄')}
            {cb('is_health_insurance_cards_insurer_number', '番号')}
            {cb('is_health_insurance_cards_insurer_name', '名称')}
          </div>

          <div className="mt-2 text-xs text-gray-500">（被保険者）</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {cb('is_health_insurance_cards_name', '氏名')}
            {cb('is_health_insurance_cards_name_kana', '氏名（フリガナ）')}
            {cb('is_health_insurance_card_sex', '性別')}
            {cb('is_health_insurance_cards_date_of_birth', '生年月日')}
            {cb('is_health_insurance_cards_address', '住所')}
            {cb('is_health_insurance_cards_address_kana', '住所（フリガナ）')}
          </div>
        </div>
      </div>

      {/* ⑤ */}
      <div>
        ⑤&nbsp;{masterCheckbox(FRONT_GROUP_5_FIELDS, '病名～転記 を印刷')}
        <div className="ml-6 flex flex-wrap gap-x-4 gap-y-1">
          {cb('is_consents_disease_name', '病名')}
          {cb('is_consents_incident_date', '発症年月日')}
          {cb('is_consents_first_treatment_date', '初療年月日')}
          {cb('is_first_visit_date', '施術開始日')}
          {cb('is_last_visit_date', '施術終了日')}
          {cb('is_consents_days', '日数')}
          {cb('is_consents_treatment_count', '施術回数')}
          {cb('is_rezepts_outcome_type_id', '転帰')}
        </div>
      </div>

      {/* ⑥ — R2 excludes 施術の種類 fields (they move to ⑦). */}
      <div>
        ⑥&nbsp;{masterCheckbox(
          isR2 ? FRONT_GROUP_6_R2_FIELDS : FRONT_GROUP_6_FIELDS,
          '同意記録 を印刷'
        )}
        <div className="ml-6 flex flex-col gap-1">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {cb('is_consents_hospital_name', '病院名')}
            {cb('is_consents_hospital_address', '住所')}
            {cb('is_consents_hospital_telephone', '電話')}
            {cb('is_consents_doctor_name', '同意医師名')}
          </div>
          <div className="flex items-center gap-2">
            {cb('is_consents_doctor_name_kana', '同意医師名（フリガナ）')}
            <AppTextField
              value={doctorNameKana}
              onChange={e => setDoctorNameKana(e.target.value)}
              maxLength={255}
              className="w-60"
              placeholder="同意医師名（フリガナ）"
            />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {cb('is_consents_consent_date', '同意')}
            {cb('is_consents_incident_cause', '発症の原因')}
            {cb('is_consents_incident_date_type_id', '第三者行為')}
            {isR2 && cb('is_consents_symptom', '同意症状')}
          </div>
          {cb('is_pre_existing_illness', '既往症・主要症状・経過等')}
          {/* A2 legacy places 往療距離 inside ⑥; R2 legacy moves it to ⑦. */}
          {!isR2 && (
            <div className="flex items-center gap-2">
              {cb('is_length', '往療距離')}
              <AppTextField
                value={length}
                onChange={e => setLength(e.target.value.replace(/[^0-9]/g, ''))}
                maxLength={10}
                className="w-32"
                placeholder="km"
              />
            </div>
          )}
        </div>
      </div>

      {/* ⑦ R2 only: 施術の種類 (マッサージ/変形徒手矯正術/温罨法/往療距離).
          Legacy R2 gates the length km textbox with @if(!empty($param['patient_id'])),
          i.e. patient-specific print only. This modal is clinic-wide, so no textbox —
          match legacy exactly. */}
      {isR2 && (
        <div>
          ⑦&nbsp;{masterCheckbox(FRONT_GROUP_7_FIELDS, '施術の種類 を印刷')}
          <div className="ml-6 flex flex-wrap gap-x-4 gap-y-1">
            {cb('is_consents_massage', 'マッサージ')}
            {cb('is_consents_orthomorphia', '変形徒手矯正術')}
            {cb('is_consents_hot_treatment', '温罨法')}
            {cb('is_length', '往療距離')}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 裏面 flags — 17 visible per service-type (BD section 27)
//   • A2: shows 「通所」 as a master checkbox driving 3 DB flags in sync
//     (is_visit_clinic_price + is_massage_price + is_orthomorphia_price).
//     Also shows day-level 一部負担金 (`is_part_of_charge_day`).
//   • R2: shows 「通所部位」/「変形徒手矯正術部位」/「温罨法又は温罨法・電療」
//     as three separate checkboxes; no day-level copayment.
// ---------------------------------------------------------------------------

interface BackGroupProps {
  flags: TreatmentRecordPrintFlags;
  setFlag: (field: PrintFlagField, checked: boolean) => void;
  isR2: boolean;
}

/** BD section 27 labels — A2 (left) and R2 (right) diverge at the three
 *  treatment-cost columns and at the「通所」(A2 only) grouping. */
const BACK_LABELS_A2: Partial<Record<BackFlagField, string>> = {
  is_back_clinic_id: '治療院ID-受診者No、受診者名',
  is_day_check: '日付',
  is_visit_clinic_price: '通所',
  is_hot_fomentation_price: '電気（針・灸）',
  is_visit_treatment_price: '訪問施術料',
  is_transportation_price: '往療料',
  is_special_area_price: '特別地域加算',
  is_subtotal_price: '合計金額（日次）',
  is_part_of_charge_day: '一部負担金（日次）',
  is_visits_massager_remarks: '施術経過所見',
  is_remarks: '備考',
  is_treatment_yearmonth: '施術年月',
  is_billing_yearmonth: '請求年月',
  is_total_price: '合計金額',
  is_part_of_charge: '一部負担金',
  is_amount_claimed: '請求金額',
  is_visits_count: '回数',
};

const BACK_LABELS_R2: Partial<Record<BackFlagField, string>> = {
  is_back_clinic_id: '治療院ID-受診者No、受診者名',
  is_day_check: '日付',
  is_massage_price: '通所部位',
  is_orthomorphia_price: '変形徒手矯正術部位',
  is_hot_fomentation_price: '温罨法又は温罨法・電療',
  is_visit_treatment_price: '訪問施術料',
  is_transportation_price: '往療料',
  is_special_area_price: '特別地域加算',
  is_subtotal_price: '合計金額（日次）',
  is_visits_massager_remarks: '施術経過所見',
  is_remarks: '備考',
  is_treatment_yearmonth: '施術年月',
  is_billing_yearmonth: '請求年月',
  is_total_price: '合計金額',
  is_part_of_charge: '一部負担金',
  is_amount_claimed: '請求金額',
  is_visits_count: '回数',
};

const BackGroup: React.FC<BackGroupProps> = ({ flags, setFlag, isR2 }) => {
  const fields = isR2 ? BACK_FLAG_FIELDS_R2 : BACK_FLAG_FIELDS_A2;
  const labels = isR2 ? BACK_LABELS_R2 : BACK_LABELS_A2;

  // A2 master「通所」reflects the AND of three flags; toggling it syncs all
  // three (BD 4.3.1 item 3 keeps `is_visit_clinic_price` / `is_massage_price` /
  // `is_orthomorphia_price` in lockstep).
  const isA2VisitClinicAllOn = !isR2 && A2_VISIT_CLINIC_SYNC_FIELDS.every(s => !!flags[s]);
  const isA2VisitClinicSomeOn = !isR2 && A2_VISIT_CLINIC_SYNC_FIELDS.some(s => !!flags[s]);

  return (
    // items-start so multi-line labels (治療院ID-受診者No、受診者名) keep the checkbox
    // anchored at the top of the row instead of centering against its 2-line height,
    // which would visually push neighbouring 1-line labels (日付, 通所) down.
    <div className="grid grid-cols-2 items-start gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
      {fields.map(f => {
        if (f === 'is_visit_clinic_price' && !isR2) {
          return (
            <AppCheckbox
              key={f}
              checked={isA2VisitClinicAllOn}
              indeterminate={!isA2VisitClinicAllOn && isA2VisitClinicSomeOn}
              onChange={e =>
                A2_VISIT_CLINIC_SYNC_FIELDS.forEach(s => setFlag(s, e.target.checked))
              }
              className="!items-start [&>span:last-child]:leading-tight"
            >
              {labels[f]}
            </AppCheckbox>
          );
        }
        return (
          <AppCheckbox
            key={f}
            checked={!!flags[f]}
            onChange={e => setFlag(f, e.target.checked)}
            className="!items-start [&>span:last-child]:leading-tight"
          >
            {labels[f]}
          </AppCheckbox>
        );
      })}
    </div>
  );
};

export default TreatmentRecordPrintModal;
